import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Logo } from '@/components/Logo';
import { RegionSelector } from '@/components/RegionSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Detect the portal mismatch payload returned by the backend on cross-region login.
// Backend returns 403 with detail like:
//   "This account belongs to the India portal. Please switch portals to continue."
//   "This account belongs to the Dubai (UAE) portal. Please switch portals to continue."
const detectPortalMismatch = (detail) => {
  if (!detail || typeof detail !== 'string') return null;
  const d = detail.toLowerCase();
  if (!d.includes('belongs to') || !d.includes('portal')) return null;
  if (d.includes('india')) return { region: 'IN', label: 'India', path: '/in/login' };
  if (d.includes('dubai') || d.includes('uae')) return { region: 'UAE', label: 'Dubai (UAE)', path: '/uae/login' };
  return null;
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  // When set, we render the portal-mismatch banner with a "Switch to X Portal" CTA.
  const [mismatch, setMismatch] = useState(null); // { region, label, path }

  // Region-prefix awareness — `/in/login` and `/uae/login` skip the
  // selector; the bare `/login` always shows it (no memory, no auto-pick).
  const path = location.pathname;
  const isInScoped = path.startsWith('/in/') || path.startsWith('/india/');
  const isUaeScoped = path.startsWith('/uae/');
  const isScoped = isInScoped || isUaeScoped;

  // After a portal switch redirect, prefill the email field and toast.
  useEffect(() => {
    try {
      const stashedEmail = sessionStorage.getItem('dreamlife_switch_email');
      const switchedTo = sessionStorage.getItem('dreamlife_switched_to');
      if (stashedEmail) {
        setEmail(stashedEmail);
        sessionStorage.removeItem('dreamlife_switch_email');
      }
      if (switchedTo) {
        toast.success(`Switched to ${switchedTo} portal — enter your password to continue.`);
        sessionStorage.removeItem('dreamlife_switched_to');
      }
    } catch { /* ignore */ }
  }, []);

  const completeLoginRedirect = async () => {
    try {
      await axios.get(`${API}/onboarding/profile`);
      toast.success('Welcome back to ASTIIV BASGAT');
      navigate('/dashboard');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.success('Welcome to ASTIIV BASGAT');
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMismatch(null);
    try {
      await login(email, password);
      await completeLoginRedirect();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const m = error.response?.status === 403 ? detectPortalMismatch(detail) : null;
      if (m) {
        // Friendly portal-switch flow — keep the user in the funnel.
        setMismatch(m);
      } else {
        toast.error(detail || error.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Switch portals via full-page navigation. We persist the email + a "switched"
  // flag to sessionStorage so the new page can prefill the email field and show
  // a friendly toast — the user just clicks "Sign In" once more. This avoids
  // brittle in-memory state across React Router transitions and guarantees the
  // axios interceptor reads the correct portal from the URL on every request.
  const handleSwitchPortal = () => {
    if (!mismatch) return;
    setSwitching(true);
    try {
      // CountryContext expects lowercase 'india' / 'uae'.
      localStorage.setItem('dreamlife_country', mismatch.region === 'IN' ? 'india' : 'uae');
      sessionStorage.setItem('dreamlife_switch_email', email);
      sessionStorage.setItem('dreamlife_switched_to', mismatch.label);
    } catch { /* ignore */ }
    window.location.assign(mismatch.path);
  };

  // ── Region gate ─────────────────────────────────────────────────────
  // When the visitor lands on the bare /login URL (no /in or /uae prefix),
  // ALWAYS show the RegionSelector first. This is intentional per the
  // ASTIIV BASGAT product mandate — never auto-redirect based on history.
  if (!isScoped) {
    return (
      <RegionSelector
        title="Sign In"
        subtitle="Select your region to continue"
        buildHref={(code) => (code === 'india' ? '/in/login' : '/uae/login')}
        bottomNote={
          <>New to ASTIIV BASGAT? <Link to="/signup" className="text-gold hover:underline">Create an account</Link></>
        }
        testIdPrefix="login-country"
      />
    );
  }

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass-card p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-3">
              <Logo to={null} size="lg" wordmarkClassName="text-gold" testId="login-logo" />
            </div>
            <p className="text-text-secondary">Sign in to your dream lifestyle</p>
          </div>

          <AnimatePresence>
            {mismatch && (
              <motion.div
                key="portal-mismatch"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="mb-6 rounded-md border border-gold/40 bg-gold/5 p-4"
                data-testid="portal-mismatch-banner"
              >
                <p className="text-sm text-text-primary leading-relaxed">
                  This account is registered on the{' '}
                  <span className="text-gold font-semibold">{mismatch.label}</span> portal.
                  Switch portals to continue with the same email and password.
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <Button
                    type="button"
                    onClick={handleSwitchPortal}
                    disabled={switching}
                    className="flex-1 bg-gold text-black hover:bg-gold-light uppercase tracking-widest text-xs font-bold py-3 rounded-sm"
                    data-testid="switch-portal-btn"
                  >
                    {switching ? 'Switching…' : `Switch to ${mismatch.label} Portal`}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setMismatch(null)}
                    disabled={switching}
                    className="flex-1 border border-white/15 text-text-secondary hover:text-text-primary hover:bg-white/5 uppercase tracking-widest text-xs font-medium py-3 rounded-sm"
                    data-testid="dismiss-mismatch-btn"
                  >
                    Use a different account
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form" autoComplete="off">
            <div>
              <Label htmlFor="email" className="text-text-primary mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                name="dreamlife_email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoComplete="new-password"
                className="bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none px-0 py-4 text-lg transition-all placeholder:text-white/20"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-text-primary mb-2 block">Password</Label>
              <Input
                id="password"
                type="password"
                name="dreamlife_password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
                className="bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none px-0 py-4 text-lg transition-all placeholder:text-white/20"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || switching}
              className="w-full bg-gold text-black hover:bg-gold-light transition-all duration-500 uppercase tracking-widest text-sm font-bold py-6 rounded-sm"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-text-secondary">
              Don't have an account?{' '}
              <Link to="/signup" className="text-gold hover:text-gold-light transition-colors" data-testid="login-signup-link">
                Create one
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-text-muted hover:text-text-secondary transition-colors text-sm" data-testid="login-back-link">
              ← Back to home
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
